import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  Stack,
  Avatar,
  Tabs,
  Tab,
  IconButton,
  LinearProgress,
  Switch,
  FormControlLabel,
  Tooltip,
  Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import InfoIcon from '@mui/icons-material/Info';
import { useAuth } from '../../components/auth/AuthContext';
import { Order } from '../../types/order';
import { Message } from '../../types/message';
import { APIHOST } from '../../common';
import { formatDateFromYYYYMMDD } from '../../../lib/datetime';
import EditOrderContactDialog from '../../components/dialog/EditOrderContactDialog';
import EditOrderBillingDialog from '../../components/dialog/EditOrderBillingDialog';
import EditOrderRoomOccupantDialog from '../../components/dialog/EditOrderRoomOccupantDialog';
import CreditCardEntry from '../../components/dialog/CreditCardEntry';
import { OrderRoom } from '@/src/types/orderRoom';
import { OrderRoomOccupant } from '@/src/types/orderRoomOccupant';
import { useApp } from '../../components/AppContext';
import MessageStatus from '../../components/MessageStatus';
import { set } from 'date-fns';

export default function UserOrderDetailPage() {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const { currentUser, isLoggedIn, loadingUser, refreshUnreadMessages } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [orderMessages, setOrderMessages] = useState<Message[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [submittingMessage, setSubmittingMessage] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editContactDialogOpen, setEditContactDialogOpen] = useState(false);
  const [editBillingDialogOpen, setEditBillingDialogOpen] = useState(false);
  const [editOccupantDialogOpen, setEditOccupantDialogOpen] = useState(false);
  const [creditCardEntryOpen, setCreditCardEntryOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string>('credit-card');
  const [updatingPaymentMethod, setUpdatingPaymentMethod] = useState(false);
  const [selectedOccupant, setSelectedOccupant] = useState<OrderRoomOccupant | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null)

  const { setSnackbarInput } = useApp();
  useEffect(() => {
    if (loadingUser) {
      return;
    }
    if (!isLoggedIn || !currentUser) {
      navigate('/');
      return;
    }
    if (orderId) {
      fetchOrder();
      fetchOrderMessages();
    }
    const interval = setInterval(fetchOrderMessages, 60000);
    return () => clearInterval(interval);
  }, [orderId, loadingUser]);

  const fetchOrder = async () => {
    if (!orderId) return;
    
    try {
      setLoading(true);

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/orders/${orderId}?populate[]=user_files.file&populate[]=user_files.user&populate[]=conference_hotel.hotel&populate[]=conference_hotel.conference&populate[]=order_rooms.conference_hotel_room&populate[]=order_rooms.order_room_occupants&populate[]=conference_group.conference&populate[]=user_files`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch order details');
      }

      const data = await response.json();
      setOrder(data.data);
      
      // Initialize payment method from order data, default to credit-card if not set
      if (data.data.paymentMethod) {
        setPaymentMethod(data.data.paymentMethod);
      }

    } catch (err) {
      setSnackbarInput({message: "Failed to load order details", severity: "error"})
      console.error('Order detail error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderMessages = async () => {
    if (!orderId) return;

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/orders/${orderId}?populate[]=messages.seenBy&populate[]=messages.author`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch order messages');
      }

      const data = await response.json();
     
      setOrderMessages(data.data.messages);
    } catch (err) {
      console.error('Fetch messages error:', err);
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'draft':
        return 'info';
      case 'received':
        return 'primary';
      case 'abandoned':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleEditOccupant = (occupant: any, room: OrderRoom) => {
    if (!room.documentId) return;
    setSelectedOccupant(occupant);
    setSelectedRoom(room.documentId);
    setEditOccupantDialogOpen(true);
  };

  const handleAddOccupant = (room: OrderRoom) => {
    if (!room.documentId) return;
    setSelectedOccupant(null); // null indicates adding a new occupant
    setSelectedRoom(room.documentId);
    setEditOccupantDialogOpen(true);
  };

  const handleDeleteOccupant = async (occupant: any) => {
    if (!window.confirm(`Are you sure you want to delete ${occupant.firstName} ${occupant.lastName}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/order-room-occupants/${occupant.documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        setSnackbarInput({message: "Failed to delete occupant", severity: "error"});
      }
      setSnackbarInput({message: "Occupant deleted successfully", severity: "success"});
      // Refresh order data
      await fetchOrder();
    } catch (err) {
      setSnackbarInput({message: "Failed to delete occupant", severity: "error"});
      console.error('Delete occupant error:', err);
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const calculateNights = (arrivalDate: string, departureDate: string) => {
    const arrival = new Date(arrivalDate);
    const departure = new Date(departureDate);
    const timeDiff = departure.getTime() - arrival.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  };

  const calculateRoomSubtotal = (dailyRate: number, nights: number) => {
    return dailyRate * nights;
  };

  const handleSubmitMessage = async () => {
    if (!newMessage.trim() || !orderId) return;

    try {
      setSubmittingMessage(true);
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: {
            message: newMessage.trim(),
            orderId: orderId,
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      // Clear the input and refresh the order to get updated messages
      setNewMessage('');
      fetchOrderMessages();
      setSnackbarInput({message: "Message sent", severity: "success"})

    } catch (err) {
      console.error('Error sending message:', err);
      setSnackbarInput({message: "Message failed to send", severity: "error"})
    } finally {
      setSubmittingMessage(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!orderId || !order) return;

    try {
      setUploadingFile(true);
      setUploadProgress(0);
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const formData = new FormData();
      formData.append('files', file);
      formData.append('order', order.documentId);

      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = (e.loaded / e.total) * 100;
          setUploadProgress(progress);
        }
      });

      xhr.open('POST', `${APIHOST}/api/file-uploads?populate=*`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.onload = function() {
        if (xhr.status === 200) {
          // Refresh the order to get updated files
          fetchOrder();
        } else {
          throw new Error('Failed to upload file');
        }
      };

      xhr.onerror = function() {
        throw new Error('Failed to upload file');
      };

      xhr.send(formData);

    } catch (err) {
      console.error('Error uploading file:', err);
      setSnackbarInput({
        severity: 'error',
        message: 'Failed to upload file. Please try again.',
      });
    } finally {
      setUploadingFile(false);
      setUploadProgress(0);
      setSnackbarInput({
        severity: 'success',
        message: 'File uploaded successfully',
      });

    }
  };

  const handleFileDownload = async (documentId: string, fileName: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/file-uploads/getUrl/${documentId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get download URL');
      }

      const data = await response.json();
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = data.url;
      link.download = data.fileName || fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      console.error('Error downloading file:', err);
      setSnackbarInput({message: "Unable to download file", severity: "error"})
    }
  };

  const handleInvoiceDownload = async () => {
    if (!orderId) return;
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/orders/${orderId}/getInvoiceURL`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get invoice URL');
      }

      const data = await response.json();
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = data.url;
      link.download = `invoice-${order?.confirmation || orderId}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      console.error('Error downloading invoice:', err);
      setSnackbarInput({message: "Unable to download invoice", severity: "error"})
    }
  };

  const handlePaymentMethodChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPaymentMethod = event.target.checked ? 'check' : 'credit-card';
    setPaymentMethod(newPaymentMethod);
    setUpdatingPaymentMethod(true);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/orders/${order?.documentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: {
            paymentMethod: newPaymentMethod
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update payment method: ${response.status} - ${response.statusText}`);
      }

      setSnackbarInput({
        message: `Payment method updated to ${newPaymentMethod === 'credit-card' ? 'Credit Card' : 'Check'}`,
        severity: 'success'
      });

      // Refresh order to get updated data
      fetchOrder();

    } catch (error) {
      console.error('Error updating payment method:', error);
      setSnackbarInput({
        message: error instanceof Error ? error.message : 'Failed to update payment method',
        severity: 'error'
      });
      // Revert the state on error
      setPaymentMethod(event.target.checked ? 'credit-card' : 'check');
    } finally {
      setUpdatingPaymentMethod(false);
    }
  };

  if (!isLoggedIn || !currentUser) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="warning">
          Please log in to view order details.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!loading && !order) {
    return (
      <Box sx={{ minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Paper sx={{ p: 3 }}>
            <Alert severity="error">Order Not Found</Alert>
            <Box sx={{ mt: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/user/orders')}
              >
                Back to Orders
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Paper sx={{ p: 3 }}>
            <Alert severity="warning">Order not found</Alert>
            <Box sx={{ mt: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/user/orders')}
              >
                Back to Orders
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/user/orders')}
            sx={{ ml: 2 }}
          >
            Back to Orders
          </Button>
        </Box>

        <Grid container spacing={3}>
          {/* Order Header */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h4" gutterBottom>
                    Order #{order.confirmation}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    {order.conference_group.conference?.longName || 'Conference'}<br/>
                    {order.conference_group.longName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Booked: {formatDateFromYYYYMMDD(order.createdAt)}
                  </Typography>
                </Box>
                <Chip 
                  label={order.orderStatus} 
                  color={getStatusColor(order.orderStatus) as any}
                  size="medium"
                />
              </Box>
            </Paper>
          </Grid>

          {/* Invoice */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Billing
                </Typography>
               
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12}}>
                      <Box sx={{float: 'right'}}>
                        <Button
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          onClick={handleInvoiceDownload}
                          size="small"
                          disabled={!order.invoicePath}
                        >
                          Download Invoice
                        </Button>
                      </Box>
                      <span>{order.invoicePath ? '✅ Invoice Available' : '❌ Invoice Not Available'}</span>
                    </Grid>
                    <Grid size={{xs: 12}}>
                      <Divider sx={{ my: 2 }} />
                    </Grid>  
                    {!order.invoiceGenerated && <Grid size={{xs: 12}}>
                        <Typography variant="body2" color="text.secondary">
                          Your invoice will be generated once your order is confirmed. You will receive an email notification when it's ready, and it will be available for download here.
                        </Typography>
                      </Grid>}
                    <Grid size={{xs: 12}}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Credit Card
                      </Typography>
                      <Box sx={{float: 'right'}}>
                        <Button
                          variant="contained"
                          onClick={() => setCreditCardEntryOpen(true)}
                          size="small"
                          startIcon={<EditIcon fontSize="small" />}
                          disabled={!order.invoiceGenerated || order.conference_hotel?.hotel.documentId === 'griapaqc47r6acf9vl8t4dbu'}
                        >
                          {order.creditCardAdded ? 'Update Credit Card' : 'Add Credit Card'}
                        </Button>
                      </Box>
                      <Box sx={{ pb: 2 }}>{order.creditCardAdded ? `✅ Credit Card Added ****${order.creditCardMasked.slice(-4)}` : '❌ Credit Card Not Added'}</Box>
                       <Typography variant="body2" color="text.secondary">
                        Credit card storage is handled by a PCI compliant third-party service and is not stored on our servers. We only store the last 4 digits of the card number and zip code for reference.
                      </Typography>
                    </Grid>

                    {!order.invoiceGenerated && <Grid size={{xs: 12}}>
                        <Typography variant="body2" color="text.secondary">
                          You cannot add or update a credit card until your invoice is generated.
                        </Typography></Grid>}
                    {order.conference_hotel?.hotel.documentId === 'griapaqc47r6acf9vl8t4dbu' && 
                      <Grid size={{xs: 12}}>
                        <Typography variant="body2" color="text.secondary">
                          Credit card cannot be added while waiting for hotel assignment.
                        </Typography>
                      </Grid>}
                    <Grid size={{xs: 12}}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Payment Method
                      </Typography>
                    </Grid>
                    <Grid size={{xs: 12}} sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body1" color={paymentMethod === 'credit-card' ? 'primary' : 'text.primary'}>
                          Credit Card
                        </Typography>
                        <Switch
                          checked={paymentMethod === 'check'}
                          onChange={handlePaymentMethodChange}
                          disabled={updatingPaymentMethod}
                          size="small"
                        />
                        <Typography variant="body1" color={paymentMethod === 'check' ? 'primary' : 'text.primary'}>
                          Check
                        </Typography>
                        {updatingPaymentMethod && <CircularProgress size={16} />}
                      </Box>
                    </Grid>
                    <Grid size={{xs: 12}}>
                      
                      <Typography variant="body2" color="text.secondary">
                        A credit card is required to hold rooms. If you plan on paying by check, the credit card on file will be used for incidentals only.
                      </Typography>
                    </Grid>
                  </Grid>
                  
      
                  <Divider sx={{ my: 2 }} />
                  {order.billingAddressee && (
                    <Grid size={{ xs: 12 }}>
                    
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" color="text.secondary">
                              Billing Contact
                            </Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => setEditBillingDialogOpen(true)}
                              sx={{ color: 'primary.main' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Box sx={{ display: 'grid', gap: 1 }}>
                            <Typography variant="body2">
                              <strong>Name:</strong> {order.billingAddressee}
                            </Typography>
                            {order.billingStreet1 && (
                              <Typography variant="body2">
                                <strong>Address:</strong> {order.billingStreet1}
                                {order.billingStreet2 && `, ${order.billingStreet2}`}
                              </Typography>
                            )}
                            {(order.billingCity || order.billingState || order.billingZip) && (
                              <Typography variant="body2">
                                <strong>City:</strong> {[order.billingCity, order.billingState, order.billingZip].filter(Boolean).join(', ')}
                              </Typography>
                            )}
                            {order.billingCountry && (
                              <Typography variant="body2">
                                <strong>Country:</strong> {order.billingCountry}
                              </Typography>
                            )}

                            <Box sx={{py: 2}}>{order.creditCardZipCode ? `✅ Credit Card Zip: ${order.creditCardZipCode}` : '❌ Credit Card Zip Code Not Added'}</Box>
                          </Box>
                     
                    </Grid>
                  )}
                
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Information */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Contact Information
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => setEditContactDialogOpen(true)}
                    sx={{ color: 'primary.main' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'grid', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>Name:</strong> {order.contactFirstName} {order.contactLastName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {order.contactEmail}
                  </Typography>
                  {order.contactCell && (
                    <Typography variant="body2">
                      <strong>Phone:</strong> {order.contactCell}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Hotel Information */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Hotel Information
                </Typography>
                <Box sx={{ display: 'grid', gap: 1 }}>
                  <Typography variant="body2">
                    {order.conference_hotel?.hotel?.name || 'TBD'}<br/>
                    {order.conference_hotel?.hotel?.addressStreet1 && (
                      <>
                        {order.conference_hotel?.hotel?.addressStreet1}<br/>
                      </>
                    )}
                    {order.conference_hotel?.hotel?.addressCity}{order.conference_hotel?.hotel?.addressState && `, ${order.conference_hotel.hotel.addressState}`} {order.conference_hotel?.hotel?.addressZip && ` ${order.conference_hotel.hotel.addressZip}`}
                  </Typography>
                  {order.conference_hotel?.invoiceNotes && (
                    <Typography variant="body2">
                      {order.conference_hotel.invoiceNotes}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Order Summary */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Order Summary
                </Typography>
                <Box sx={{ display: 'grid', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>Rooms:</strong> {order.order_rooms?.length || 0}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Bus Parking:</strong> {order.requiresBusParking ? 'Required' : 'Not required'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Transit to Venue:</strong> {order.requiresTransitToVenue ? 'Required' : 'Not required'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Room Details */}
          {order.order_rooms && order.order_rooms.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Room Details ({order.order_rooms.length} room{order.order_rooms.length !== 1 ? 's' : ''})
                  </Typography>
                  {order.order_rooms.map((room, index) => {
                    const nights = calculateNights(room.arrivalDate, room.departureDate);
                    const subtotal = room.dailyRate ? calculateRoomSubtotal(room.dailyRate, nights) : 0;
                    
                    return (
                      <Box 
                        key={room.id}
                        sx={{ 
                          mb: 3, 
                          p: 3, 
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          boxShadow: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="h6" color="primary">
                            Room {index + 1}
                          </Typography>
                          <Chip 
                            label={room.holdStatus} 
                            size="small" 
                            color={room.holdStatus === 'held' ? 'success' : 'default'}
                          />
                        </Box>

                        <Grid container spacing={2}>
                          {/* Room Description */}
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="body1" fontWeight={"600"} gutterBottom>
                                {room.conference_hotel_room?.name || 'TBD'} - {room.type}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" gutterBottom mb={3}>
                                <strong>Max Occupants:</strong> {room.conference_hotel_room?.maxOccupants || 'Not specified'}
                              </Typography>
                              <Box sx={{ display: 'grid', gap: 1 }}>
                                <Typography variant="body2">
                                  <strong>Check-in:</strong> {formatDateFromYYYYMMDD(room.arrivalDate)}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Check-out:</strong> {formatDateFromYYYYMMDD(room.departureDate)}
                                </Typography>
                              </Box>
                            </Card>
                          </Grid>

                          {/* Pricing Information */}
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Pricing
                              </Typography>
                              <Box sx={{ display: 'grid', gap: 1 }}>
                                <Typography variant="body2">
                                  <strong>Daily Rate:</strong> {room.dailyRate ? `$${room.dailyRate.toFixed(2)}` : 'Not specified'}
                                </Typography>
                                {room.dailyRate && (
                                  <>
                                    <Typography variant="body2">
                                      <strong>Nights:</strong> {nights}
                                    </Typography>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="body1" fontWeight="600" color="primary">
                                      <strong>Room Subtotal:</strong> ${subtotal.toFixed(2)}
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            </Card>
                          </Grid>

                          {/* Occupants List */}
                          <Grid size={{ xs: 12 }}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Occupants ({room.order_room_occupants?.length || 0})
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<PersonAddIcon fontSize="small" />}
                                  onClick={() => handleAddOccupant(room)}
                                  sx={{ fontSize: '0.75rem' }}
                                >
                                  Add Occupant
                                </Button>
                              </Box>
                              {room.order_room_occupants && room.order_room_occupants.length > 0 ? (
                                <Box sx={{ display: 'grid', gap: 1 }}>
                                  {room.order_room_occupants.map((occ, occIndex) => (
                                    <Box 
                                      key={occ.id || occIndex}
                                      sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        py: 1,
                                        px: 2,
                        
                                      }}
                                    >
                                      <Typography variant="body2" fontWeight="500">
                                        {occ.firstName} {occ.lastName}
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleEditOccupant(occ, room)}
                                          sx={{ color: 'primary.main' }}
                                          title="Edit occupant"
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDeleteOccupant(occ)}
                                          sx={{ color: 'error.main' }}
                                          title="Delete occupant"
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  No occupants assigned to this room
                                </Typography>
                              )}
                            </Card>
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })}

                  {/* Order Total Section */}
                  {order.order_rooms.some(room => room.dailyRate) && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Order Total
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1">
                          Total for {order.order_rooms.length} room{order.order_rooms.length !== 1 ? 's' : ''}:
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          ${order.order_rooms.reduce((total, room) => {
                            if (room.dailyRate) {
                              const nights = calculateNights(room.arrivalDate, room.departureDate);
                              return total + calculateRoomSubtotal(room.dailyRate, nights);
                            }
                            return total;
                          }, 0).toFixed(2)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        * This total does not include taxes, fees, or additional charges that may apply.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Notes */}
          {order.notesForHotel && (
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Notes for Hotel
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    {order.notesForHotel}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          {/* Messages and Files Tabs */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                  <Tab label="Messages" />
                  <Tab label="Files" />
                </Tabs>
              </Box>
              
              {/* Messages Tab */}
              {activeTab === 0 && (
                <CardContent>
                  {orderMessages && orderMessages.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column-reverse',overflowY: 'auto', height: '400px', mb: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3}}>
                      {orderMessages.map((message, index) => {
                        const isCurrentUser = currentUser && message.author?.id === currentUser.id;
                        
                        return (
                          <Box 
                            key={message.id} 
                            sx={{ 
                              display: 'flex',
                              justifyContent: isCurrentUser ? 'flex-start' : 'flex-end'
                            }}
                          >
                            <Card 
                              variant="outlined" 
                              sx={{ 
                                p: 2,
                                maxWidth: '75%',
                                bgcolor: isCurrentUser ? 'primary.50' : 'background.paper'
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexDirection: isCurrentUser ? 'row-reverse' : 'row' }}>
                                <Avatar sx={{ width: 36, height: 36 }}>
                                  {message.author?.firstName?.charAt(0) || message.author?.email?.charAt(0) || 'U'}
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexDirection: isCurrentUser ? 'row-reverse' : 'row' }}>
                                    <Typography variant="body2" fontWeight="medium">
                                      {message.author?.firstName && message.author?.lastName 
                                        ? `${message.author.firstName} ${message.author.lastName}`
                                        : message.author?.email || 'Unknown'
                                      }
                                    </Typography>
                                    {message.author?.userType === 'internal' && (
                                      <Chip 
                                        label="Staff" 
                                        size="small" 
                                        color="primary" 
                                        sx={{ height: 20 }}
                                      />
                                    )}
                                    
                                  </Box>
                                  <MessageStatus message={message} user={currentUser} onUpdate={() => {fetchOrderMessages();refreshUnreadMessages()}}/>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1, textAlign: isCurrentUser ? 'right' : 'left' }}>
                                    {message.message}
                                  </Typography>
                                </Box>
                              </Box>
                            </Card>
                          </Box>
                        );
                      })}
                    </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 3 }}>
                      No messages
                    </Typography>
                  )}
                  
                  {/* Message Input Form */}
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      placeholder="Type your message here..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={submittingMessage}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        endIcon={<SendIcon />}
                        onClick={handleSubmitMessage}
                        disabled={submittingMessage || !newMessage.trim()}
                      >
                        {submittingMessage ? 'Sending...' : 'Send Message'}
                      </Button>
                    </Box>
                  </Stack>
                </CardContent>
              )}

              {/* Files Tab */}
              {activeTab === 1 && (
                <CardContent>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Upload Files
                    </Typography>
                    <input
                      accept="*/*"
                      style={{ display: 'none' }}
                      id="file-upload-input"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleFileUpload(file);
                          // Reset the input
                          e.target.value = '';
                        }
                      }}
                    />
                    <label htmlFor="file-upload-input">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<AttachFileIcon />}
                        disabled={uploadingFile}
                        size={'small'}
                      >
                        Choose File
                      </Button>
                    </label>
                    {uploadingFile && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Uploading... {Math.round(uploadProgress)}%
                        </Typography>
                        <LinearProgress variant="determinate" value={uploadProgress} />
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="h6" gutterBottom>
                    Uploaded Files
                  </Typography>
                  {order.user_files && order.user_files.length > 0 ? (
                    <List>
                      {order.user_files.map((userFile, index) => {
                       
                        
                        return (
                          <Box key={userFile.id}>
                            <ListItem
                              sx={{ px: 0 }}
                              secondaryAction={
                                <Box>
                                  <IconButton
                                    edge="end"
                                    aria-label="download"
                                    onClick={() => handleFileDownload(userFile.documentId, userFile.title)}
                                    sx={{ ml: 1 }}
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </Box>
                              }
                            >
                              <ListItemText
                                disableTypography
                                primary={userFile.title}
                                secondary={
                                  <Box>
                                    <Typography variant="body2" color="text.secondary">
                                      {userFile.user.firstName && userFile.user.lastName
                                        ? `Uploaded by ${userFile.user.firstName} ${userFile.user.lastName}`
                                        : 'Unknown uploader'
                                      }
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {/* Size: {(userFile.file.size / 1024).toFixed(1)} KB */}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Uploaded: {formatDateTime(userFile.createdAt)}
                                    </Typography>

                                  </Box>
                                }
                              />
                            </ListItem>
                            {index < order.user_files!.length - 1 && <Divider />}
                          </Box>
                        );
                      })}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No files uploaded
                    </Typography>
                  )}
                </CardContent>
              )}
            </Card>
          </Grid>
    
        </Grid>
      </Container>

      {/* Edit Contact Dialog */}
      {order && (
        <EditOrderContactDialog
          open={editContactDialogOpen}
          onClose={() => {
            setEditContactDialogOpen(false);
            // Refresh order data after edit
            fetchOrder();
          }}
          orderId={order.documentId}
        />
      )}

      {/* Edit Billing Dialog */}
      {order && (
        <EditOrderBillingDialog
          open={editBillingDialogOpen}
          onClose={() => {
            setEditBillingDialogOpen(false);
            // Refresh order data after edit
            fetchOrder();
          }}
          orderId={order.documentId}
        />
      )}

      {/* Edit Occupant Dialog */}
      {selectedRoom && (
        <EditOrderRoomOccupantDialog
          open={editOccupantDialogOpen}
          onClose={() => {
            setEditOccupantDialogOpen(false);
            setSelectedOccupant(null);
            setSelectedRoom(null);

          }}
          occupant={selectedOccupant} // null for adding new, object for editing existing
          orderRoomId={selectedRoom}
          onSave={() => {
            setEditOccupantDialogOpen(false);
            setSelectedOccupant(null);
            setSelectedRoom(null);
            // Refresh order data after edit
            fetchOrder();
          }}
        />
      )}

      {/* Credit Card Entry Dialog */}
      {orderId && (
        <CreditCardEntry
          open={creditCardEntryOpen}
          onClose={() => {setCreditCardEntryOpen(false); fetchOrder(); }}
          orderId={orderId}
        />
      )}
    </Box>
  );
}